import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import {
  ShieldCheck, FileSearch, Users, Package, ArrowLeft, CheckCircle2,
  Wrench, Truck, ClipboardCheck, Factory, Cog, LineChart,
} from "lucide-react";

const TRUST = [
  { icon: Factory, title: "جهات تصنيع مؤهلة", desc: "شبكة ورش مختارة بعناية عبر المملكة" },
  { icon: ShieldCheck, title: "سرية الملفات الفنية", desc: "التزام بالمحافظة على رسوماتك وبيانات منشأتك" },
  { icon: ClipboardCheck, title: "متابعة مراحل التصنيع", desc: "شفافية كاملة من الطلب حتى التسليم" },
  { icon: Cog, title: "فحص الجودة قبل التسليم", desc: "مطابقة مواصفات وقياسات دقيقة" },
  { icon: Wrench, title: "حلول للقطع النادرة والمتوقفة", desc: "هندسة عكسية وتصنيع بديل موثوق" },
  { icon: Package, title: "إعادة تصنيع سابقة بسهولة", desc: "أرشيف رقمي لقطعك السابقة" },
];

const STEPS = [
  { n: "1", title: "أرسل طلبك", desc: "ارفع الصور أو الرسومات والمقاسات وحدد الكمية المطلوبة." },
  { n: "2", title: "الفحص الفني", desc: "يقوم فريق MERQAV بمراجعة الطلب أو زيارة موقع العميل عند الحاجة." },
  { n: "3", title: "اختيار جهة التصنيع", desc: "يتم إرسال الطلب بشكل سري إلى الجهات المؤهلة المناسبة." },
  { n: "4", title: "التصنيع والمتابعة", desc: "نتابع مراحل الإنتاج ونوثق التحديثات والفحص الفني." },
  { n: "5", title: "الفحص والتسليم", desc: "تخضع القطعة للفحص قبل تسليمها إلى العميل." },
];

const INDUSTRIES = [
  "المصانع", "شركات المقاولات", "محطات المياه", "محطات الطاقة", "النفط والغاز",
  "التعدين", "الأغذية والمشروبات", "الأدوية", "مواد البناء", "الخدمات اللوجستية",
  "المعدات الثقيلة", "الزراعة", "الصيانة الصناعية", "التكييف والتبريد", "خطوط الإنتاج",
];

const CAPS = [
  "خراطة CNC", "تفريز CNC", "قص بالليزر", "قص بالماء", "لحام", "صب المعادن",
  "قطع الألمنيوم", "الفولاذ المقاوم", "المطاط الصناعي", "الجوانات وحلقات الإحكام",
  "قطع البلاستيك والنايلون", "الحقن البلاستيكي", "الطباعة ثلاثية الأبعاد", "الهندسة العكسية",
  "المعالجة الحرارية", "الطلاء بالبودرة والجلفنة",
];

export default function HomePage() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="hero-blueprint text-white" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-amber/15 border border-brand-amber/30 text-brand-amber px-3 py-1.5 rounded-md text-xs font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-brand-amber animate-pulse" />
              منصة سعودية للتصنيع الصناعي
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="hero-headline">
              نربط الصناعة... <br/>
              <span className="text-brand-amber">ونبني الثقة.</span>
            </h1>
            <div className="text-sm text-slate-400 mb-4">Connecting Industry. Building Trust.</div>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl">
              ارفع صور القطعة أو الرسومات الفنية، أو اطلب زيارة فنية، وسيتولى فريق MERQAV دراسة الطلب،
              واختيار جهة التصنيع المناسبة، ومتابعة التنفيذ والفحص حتى التسليم.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-request">
                <Button className="btn-cta text-base px-6 py-6">
                  اطلب تصنيع قطعة <ArrowLeft className="w-4 h-4 ms-2 rtl-flip" />
                </Button>
              </Link>
              <Link to="/register?service=inspection" data-testid="hero-cta-inspection">
                <Button variant="outline" className="border-white/40 text-white bg-white/5 hover:bg-white/10 hover:text-white px-6 py-6 text-base">
                  اطلب زيارة فنية
                </Button>
              </Link>
              <Link to="/register?role=workshop" data-testid="hero-cta-partner">
                <Button variant="ghost" className="text-white hover:bg-white/10 px-6 py-6 text-base">
                  انضم كشريك تصنيع
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MERQAV Story — brand refresh section */}
      <section className="py-20 bg-white relative overflow-hidden" data-testid="brand-story-section">
        <div className="absolute inset-y-0 start-0 w-1 bg-brand-gold hidden lg:block" />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl bg-brand-navy p-10 text-white overflow-hidden shadow-xl" data-testid="brand-story-visual">
              <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full bg-brand-gold/25 blur-3xl" />
              <div className="absolute -bottom-20 -start-10 w-64 h-64 rounded-full bg-brand-gold/10 blur-3xl" />
              <div className="relative">
                <div className="text-6xl sm:text-7xl font-black tracking-tight leading-none">MERQAV</div>
                <div className="mt-3 h-1 w-24 bg-brand-gold rounded-full" />
                <div className="mt-4 text-sm text-slate-300">Connecting Industry. Building Trust.</div>
                <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-brand-gold">+150</div>
                    <div className="text-[11px] text-slate-400 mt-1">ورشة مؤهلة</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-brand-gold">15+</div>
                    <div className="text-[11px] text-slate-400 mt-1">قطاع صناعي</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-brand-gold">100%</div>
                    <div className="text-[11px] text-slate-400 mt-1">سرية للملفات</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs font-bold tracking-widest text-brand-gold mb-3">قصة MERQAV</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-navy leading-tight mb-5" data-testid="brand-story-title">
              منصة سعودية واحدة تربط الصناعة بأكملها.
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              انطلقت <span className="font-semibold text-brand-navy">MERQAV</span> من قناعة بأن أهم القطع الصناعية
              يجب أن تُصنع بثقة، وسرية، وبمعايير جودة عالية — دون الحاجة لاتصال مباشر بين المصنع والورشة.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              نُدير كامل الرحلة عنك: من فهم المتطلب واختيار الورشة الأنسب، إلى المتابعة الفنية،
              والفحص، والتسليم. كل ذلك عبر منصة رقمية واحدة، بمعايير المملكة العربية السعودية،
              وبأصول لا نتنازل عنها: الجودة، والسرية، والالتزام.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-7">
              <div className="border-s-2 border-brand-gold ps-4">
                <div className="text-brand-navy font-bold mb-1">الجودة</div>
                <div className="text-xs text-slate-600 leading-relaxed">فحص مستقل قبل التسليم</div>
              </div>
              <div className="border-s-2 border-brand-gold ps-4">
                <div className="text-brand-navy font-bold mb-1">السرية</div>
                <div className="text-xs text-slate-600 leading-relaxed">هوية الأطراف محمية دائماً</div>
              </div>
              <div className="border-s-2 border-brand-gold ps-4">
                <div className="text-brand-navy font-bold mb-1">الالتزام</div>
                <div className="text-xs text-slate-600 leading-relaxed">مواعيد تسليم واضحة وموثوقة</div>
              </div>
            </div>
            <Link to="/about" data-testid="brand-story-cta">
              <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white">
                تعرّف على MERQAV أكثر <ArrowLeft className="w-4 h-4 ms-2 rtl-flip" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust indicators */}
      <section className="py-16 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {TRUST.map((t) => (
              <div key={t.title} className="card-industrial p-6" data-testid={`trust-card-${t.title}`}>
                <div className="w-11 h-11 rounded-md bg-brand-amber/10 flex items-center justify-center mb-4">
                  <t.icon className="w-6 h-6 text-brand-amber" />
                </div>
                <h3 className="text-lg font-semibold text-brand-navy mb-1">{t.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50 grid-lines-bg" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs font-bold tracking-widest text-brand-amber mb-3">كيف تعمل MERQAV</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-navy">خمس خطوات موثوقة من الطلب إلى التسليم</h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2">
            {STEPS.map((s) => (
              <div key={s.n} className="card-industrial p-6" data-testid={`step-${s.n}`}>
                <div className="text-4xl font-bold text-brand-amber mb-2">{s.n}</div>
                <h3 className="font-semibold text-brand-navy mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-xs font-bold tracking-widest text-brand-amber mb-3">القطاعات المخدومة</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-navy">نخدم أهم القطاعات الصناعية في المملكة</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {INDUSTRIES.map((i) => (
              <div key={i} className="card-industrial p-4 flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-brand-amber shrink-0" />
                <span className="text-sm font-medium text-slate-700">{i}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities preview */}
      <section className="py-20 bg-slate-50" data-testid="capabilities-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              <div className="text-xs font-bold tracking-widest text-brand-amber mb-3">القدرات التصنيعية</div>
              <h2 className="text-3xl font-bold text-brand-navy mb-4">شبكة تصنيع متكاملة</h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                من الخراطة والتفريز إلى الهندسة العكسية والمعالجات السطحية، تُدير MERQAV شبكة واسعة
                من ورش التصنيع المؤهلة القادرة على تنفيذ متطلباتك بدقة عالية.
              </p>
              <Link to="/capabilities">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white" data-testid="view-capabilities-btn">
                  عرض جميع القدرات <ArrowLeft className="w-4 h-4 ms-2 rtl-flip" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
              {CAPS.map((c) => (
                <div key={c} className="card-industrial p-4 text-center" data-testid={`cap-${c}`}>
                  <Cog className="w-5 h-5 text-brand-amber mx-auto mb-2" />
                  <span className="text-sm font-medium text-slate-700">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-brand-navy" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">نربط الصناعة... ونبني الثقة.</h2>
          <p className="text-slate-300 mb-8 text-lg">ابدأ الآن وأرسل طلبك خلال دقائق. سيتواصل معك فريقنا لمتابعة التفاصيل.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="final-cta-register">
              <Button className="btn-cta px-8 py-6 text-base">إنشاء حساب مصنع</Button>
            </Link>
            <Link to="/register?role=workshop" data-testid="final-cta-partner">
              <Button variant="outline" className="border-white/40 text-white bg-white/5 hover:bg-white/10 hover:text-white px-8 py-6 text-base">
                انضم كشريك تصنيع
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
